import { makeAutoObservable, runInAction } from 'mobx'
import { API_URL_MICROSERVICE } from 'shared/config'
import { RequestLoadData } from 'shared/api/lib'
import {
  type IIntegrationOrganizationSettingsResponse,
  type IIntegrationOrganizationSettingType,
  type Integration,
  IntegrationsApi,
} from 'entities/Integrations'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { SALESFORCE_CONNECTION_CONFIG } from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationSalesforceSettings/lib'

const DEFAULT_PACKAGE_TYPE = 'premium'

export class IntegrationSalesforceSettingsInstallationStore {
  private _packageType: IIntegrationOrganizationSettingType = DEFAULT_PACKAGE_TYPE
  private _connectionOptions = SALESFORCE_CONNECTION_CONFIG

  constructor(private _integration: Integration, private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  init = () => {
    this._getData()
  }

  private _requestGetStatus = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      const [settings, version] = await Promise.all([
        IntegrationsApi.getIntegrationOrganizationSetting({
          cancelToken: cancelRequest.token,
        }),
        IntegrationsApi.getIntegrationVersion(this._integration.key, {
          cancelToken: cancelRequest.token,
        }),
      ])

      runInAction(() => {
        this._connectionOptions = SALESFORCE_CONNECTION_CONFIG.map((config) => ({
          ...config,
          connectionLink: this._getConnectUrl(config.id === 'sandbox'),
          settingsLink: this._getSettingUrl(),
          ...(config.versionKey ? { downloadLink: String(version.data[config.versionKey]) } : {}),
        }))
      })

      this._setPackageTypeFromRequest(settings.data)
    },
  })

  private _getData = () => {
    return this._requestGetStatus.loadData()
  }

  private _requestUpdateStatus = new RequestLoadData<IIntegrationOrganizationSettingType>({
    requestCallback: async (config, value) => {
      if (!value) return

      const response = await IntegrationsApi.putIntegrationOrganizationSetting({
        key: 'salesforce_package_type',
        value,
      })

      if (response.status !== 200) {
        this._setPackageTypeFromRequest(response.data)
      }
    },
  })

  private _updateData = (value: IIntegrationOrganizationSettingType) => {
    return this._requestUpdateStatus.loadData(value)
  }

  private _getConnectUrl(isSandbox = false) {
    let url = `${API_URL_MICROSERVICE}core/integrations/${this._integration.key}/connect`
    if (this._integration.key === 'salesforce') {
      url = `${API_URL_MICROSERVICE}integration/connect/${this._integration.key}`
    }
    if (isSandbox) {
      url += '?is_sandbox=true'
    }
    return url
  }

  private _getSettingUrl() {
    let url = ''

    if (this._integration.connected) {
      url = `${
        new URL(this._integration.connected.url).origin
      }/lightning/n/Salesmsg_App__Salesmsg_Home`
    }

    return url
  }

  private _setPackageTypeFromRequest = (response: IIntegrationOrganizationSettingsResponse) => {
    const item = response.data[0]
    if (item && item.key === 'salesforce_package_type') {
      runInAction(() => {
        this._packageType = item.value
      })
    }
  }

  setPackageType = (value: IIntegrationOrganizationSettingType) => {
    this._packageType = value
    this._updateData(value)
  }

  get packageType() {
    return this._packageType
  }

  get hasConnectionLimit() {
    return (
      (this._entities.subscriptionStore.isTrial || this._entities.subscriptionStore.isPAYGPlan) &&
      !!this._entities.integrationsStore.connectedIntegrations.length
    )
  }

  get currentConnection() {
    return (
      this._connectionOptions.find((item) => item.id === this.packageType) ||
      this._connectionOptions[0]
    )
  }

  get selectItems() {
    return this._connectionOptions.map((item) => ({
      id: item.id,
      label: item.label,
    }))
  }

  get isConnected() {
    return this._integration.isConnected
  }

  get isLimitedIntegration() {
    const packageType = this._packageType
    return packageType === 'sandbox'
  }
}
